import React, { useEffect, useState, useMemo } from 'react';
import { Drawer } from 'components/Elements';
import { formatAccountAge } from 'utils/formatAccountAge';
import { useCustomerDetails } from '../api/getCustomerDetails';
import { FaFemale, FaMale } from 'react-icons/fa';
import { MdOpenInNew } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { imageToBlob } from 'utils/ImageToBlob';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const UserPhoto = ({ photoUrl, initials }) => (
  <div className="w-32 aspect-square rounded-full bg-white/10 backdrop-blur-md border border-white/10 flex items-center justify-center overflow-hidden">
    {photoUrl ? (
      <img
        src={photoUrl}
        alt="User Profile"
        className="w-full h-full object-cover"
      />
    ) : (
      <span className="text-4xl text-white font-bold">{initials}</span>
    )}
  </div>
);

const UserStatus = ({ status }) => (
  <div className={`text-xs rounded-full p-1 px-3 w-fit ${status === 1 ? 'bg-green-500' : 'bg-red-500'} text-white`}>
    {status === 1 ? 'Active' : 'Inactive'}
  </div>
);

const UserMeta = ({ email, phone, gender }) => (
  <>
    <div className="text-white/60">{email}</div>
    <div className="text-white/80 font-bold">{phone}</div>
    <div className="rounded-full border border-white/20 flex items-center gap-2 p-1 text-xs w-fit px-2">
      {gender === 'm' ? <FaMale /> : <FaFemale />}
      <span>{gender === 'm' ? 'Male' : 'Female'}</span>
    </div>
  </>
);

export const UserDetailsDrawer = ({ isOpen, onClose, id }) => {
  const { data, isLoading } = useCustomerDetails({ id });
  const user = data?.userDetails;
  const navigate = useNavigate();

  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);

  const initials = useMemo(() => {
    if (!user) return '';
    return `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`;
  }, [user]);

  useEffect(() => {
    const fetchImage = async () => {
      if (user?.photo) {
        setImageLoading(true);
        const localURL = await imageToBlob(user.photo);
        if (localURL) {
          setImageUrl(localURL);
        }
        setImageLoading(false);
      }
    };
    fetchImage();
  }, [user?.photo]);

  const renderFooter = () => (
    <button
      onClick={() => navigate(`/app/users/${id}`)}
      className="text-sm flex flex-row items-center space-x-2 text-white"
    >
      <MdOpenInNew />
      <span>View Details</span>
    </button>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={
        isLoading || !user
          ? 'Loading user...'
          : `${user?.userName} - #${user?.userCode}`
      }
      size="sm"
      renderFooter={renderFooter}
    >
      <div className="space-y-4">
        {isLoading || !user ? (
          <Skeleton height={200} />
        ) : (
          <>
            <UserPhoto photoUrl={imageUrl} initials={initials} />

            <div className="flex flex-row items-center space-x-2">
              <div className="capitalize font-bold text-lg">
                {`${user.firstName} ${user.middleName} ${user.lastName}`}
              </div>
              <UserStatus status={user.status} />
            </div>

            <UserMeta
              email={user.email}
              phone={user.phoneNumber}
              gender={user.gender}
            />

            <div className="text-xs mt-3 text-white/80">
              Account is {formatAccountAge(user.createdAt)} old
            </div>

            <hr className="mt-4 mb-2 border border-white/10" />

            <section>
              <div className="text-md font-semibold text-white/90">Activity</div>
              <em className="text-xs font-light text-white/90">
                No activity logged for this user at the moment.
              </em>
            </section>

            <hr className="mt-4 mb-2 border border-white/10" />

            <section>
              <div className="text-md font-semibold text-white/90">
                Pending Transactions
              </div>
              <em className="text-xs font-light text-white/90">
                No pending transactions for this user at the moment.
              </em>
            </section>
          </>
        )}
      </div>
    </Drawer>
  );
};
